import {
  apiContainer,
  queryBuilder,
  massageSingleRecordBeforeUpsert
} from './apiContainer'
import { ArtificialDetailPropertyNames } from '../../common/globalNotary'
import uuid from 'react-uuid'

const SLICE_API_ROOT = '/skillsets'
const PARTY_BATCH_SIZE = 100

export const fetchPartiesByIds = async (fetchWithBQ, partyIds) => {
  const uniquePartyIds = [...new Set(partyIds.filter(Boolean))]

  if (!uniquePartyIds.length) return { data: [], error: null }

  const batches = Array.from(
    { length: Math.ceil(uniquePartyIds.length / PARTY_BATCH_SIZE) },
    (_, index) =>
      uniquePartyIds.slice(
        index * PARTY_BATCH_SIZE,
        (index + 1) * PARTY_BATCH_SIZE
      )
  )
  const responses = await Promise.all(
    batches.map(ids =>
      fetchWithBQ(
        queryBuilder('/parties', {
          ids,
          per_page: PARTY_BATCH_SIZE
        })
      )
    )
  )
  const error = responses.find(response => response?.error)?.error ?? null
  const data = responses.flatMap(response => response?.data?.data ?? [])

  return { data, error }
}

export const decorateSkillsetParties = (associations, parties) =>
  associations.flatMap(association => {
    const party = parties.find(item => item.id === association.party_id)

    if (!party) return []

    return [
      {
        ...association,
        name: [party.first_name, party.last_name].filter(Boolean).join(' ').trim()
      }
    ]
  })

const skillsetApi = apiContainer.injectEndpoints({
  endpoints: build => ({
    listSkillsets: build.query({
      query: args => queryBuilder(SLICE_API_ROOT, args)
    }),

    getOneSkillset: build.query({
      async queryFn (_arg, _queryApi, _extraOptions, fetchWithBQ) {
        const res = await Promise.all([
          fetchWithBQ(`${SLICE_API_ROOT}/${_arg.id}`),
          fetchWithBQ(`/parties/~/skillsets?skillset_id=${_arg.id}`)
        ])

        const primaryData = res[0].data.data
        const associations = res[1].data.data
        const { data: parties, error } = await fetchPartiesByIds(
          fetchWithBQ,
          associations.map(association => association.party_id)
        )

        if (error) return { error }

        const decoratedParties = decorateSkillsetParties(
          associations,
          parties
        )

        primaryData[ArtificialDetailPropertyNames.DETACHED_DEPENDENCY1] =
          decoratedParties

        primaryData[
          ArtificialDetailPropertyNames.DETACHED_DEPENDENCY1_INITIAL_VALUES
        ] = decoratedParties

        // tmp fix for saving auxiliary updates
        return { data: { data: { ...primaryData, _objectId: uuid() } } }
      }
    }),
    bulkUpdateSkillsets: build.mutation({
      query: args => {
        return {
          url: SLICE_API_ROOT,
          method: 'PATCH',
          body: { data: args }
        }
      }
    }),
    upsertSkillset: build.mutation({
      query: args => {
        const { id } = args
        const actualArgs = massageSingleRecordBeforeUpsert(args)

        if (id) {
          // update

          return {
            url: `${SLICE_API_ROOT}/${id}`,
            method: 'PUT',
            body: actualArgs
          }
        } //insert
        else {
          return {
            url: SLICE_API_ROOT,
            method: 'POST',
            body: actualArgs
          }
        }
      }
    }),
    deleteSkillset: build.mutation({
      query: args => {
        return {
          url: `${SLICE_API_ROOT}/${args.id}`,
          method: 'DELETE'
        }
      }
    })
  }),
  overrideExisting: true
})

export const {
  useGetOneSkillsetQuery,
  useLazyListSkillsetsQuery,
  useUpsertSkillsetMutation,
  useDeleteSkillsetMutation,
  useBulkUpdateSkillsetsMutation
} = skillsetApi
